import React, { useState, useEffect } from 'react';
import {
  Grid, Box, TextField, Select, MenuItem, FormControl, InputLabel,
  Typography, Dialog, DialogTitle, DialogContent, DialogActions,
  Autocomplete, CircularProgress, Chip,
} from "@mui/material";
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import Button from '@mui/material/Button';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import { ACTIONS } from '../../../../shared/constants';
import { getUser } from '../../../../shared/auth-service';
import { getallemployeesWithAirports } from '../logic';

const ManagementAirportBulkAssign = ({
  handleClose,
  handleSave,
  airports,
}) => {
  const userData = getUser();
  const companyId = userData.countryCode.split('-')[0];

  const [selectedBase, setSelectedBase] = useState(userData.airport?.id || 0);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [selectedAirports, setSelectedAirports] = useState([]);
  const [selectedAirportValue, setSelectedAirportValue] = useState(null);
  const [isDirty, setIsDirty] = useState(false);
  const [showCancelAlert, setShowCancelAlert] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoadingEmployees(true);
      const data = await getallemployeesWithAirports(selectedBase, companyId);
      setEmployees(data || []);
      setLoadingEmployees(false);
    };
    load();
  }, [selectedBase]);

  const handleAddAirport = (airportObj) => {
    if (!airportObj || selectedAirports.some(a => a.id === airportObj.id)) return;
    setSelectedAirports(prev => [...prev, { id: airportObj.id, code: airportObj.code }]);
    setIsDirty(true);
  };

  const onSave = () => {
    if (selectedEmployees.length === 0 || selectedAirports.length === 0) return;
    handleSave({
      employeeIds: selectedEmployees.map(e => e.employeeId),
      airports: selectedAirports.map(a => ({ Id: a.id, Code: a.code }))
    });
  };

  return (
    <>
      <Dialog maxWidth="md" fullWidth open>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <GroupAddIcon sx={{ color: '#00b0ef' }} />
            <Typography variant="h6" fontWeight="bold" style={{ color: '#1a3072' }}>
              Asignación de <span style={{ color: '#00b0ef' }}>Aeropuertos</span>
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent>
          {/* Selección de empleados */}
          <Box sx={{ mb: 2, p: 2, bgcolor: '#f5f7fa', borderRadius: 2, border: '1px solid #e0e4ed' }}>
            <Typography variant="subtitle2" sx={{ mb: 1.5, color: '#1a3072', fontWeight: 'bold', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 1 }}>
              Empleados
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={3}>
                <Autocomplete
                  size="small"
                  disableClearable
                  options={[{ id: 0, code: 'Todas' }, ...airports]}
                  getOptionLabel={(o) => o.code || ''}
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                  value={[{ id: 0, code: 'Todas' }, ...airports].find(a => a.id === selectedBase) || null}
                  onChange={(_, newValue) => { setSelectedBase(newValue?.id ?? 0); setSelectedEmployees([]); }}
                  renderInput={(params) => <TextField {...params} label="Base" size="small" />}
                  noOptionsText="Sin resultados"
                />
              </Grid>
              <Grid item xs={12} sm={9}>
                {loadingEmployees ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                    <CircularProgress size={20} />
                    <Typography variant="body2">Cargando empleados...</Typography>
                  </Box>
                ) : (
                  <Autocomplete
                    multiple
                    size="small"
                    options={employees}
                    getOptionLabel={(o) => `${o.employeeId} - ${o.name} ${o.lastName}`}
                    value={selectedEmployees}
                    onChange={(_, newValue) => { setSelectedEmployees(newValue); setIsDirty(true); }}
                    isOptionEqualToValue={(o, v) => o.employeeId === v.employeeId}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          key={option.employeeId}
                          label={`${option.employeeId} - ${option.name} ${option.lastName}`}
                          size="small"
                          {...getTagProps({ index })}
                          sx={{ bgcolor: '#e8f0fe', color: '#1a3072' }}
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={`Empleados seleccionados (${selectedEmployees.length})`}
                        placeholder={selectedEmployees.length === 0 ? 'Buscar y seleccionar...' : ''}
                      />
                    )}
                    noOptionsText="Sin resultados"
                  />
                )}
              </Grid>
              {selectedEmployees.length === 1 && selectedEmployees[0]?.employeePosition?.name && (
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 0.5 }}>
                    <Typography variant="caption" sx={{ color: '#888', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                      Cargo:
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#1a3072', fontWeight: 500 }}>
                      {selectedEmployees[0].employeePosition.name}
                    </Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
          </Box>

          {/* Selección de aeropuertos */}
          <Box sx={{ p: 2, border: '1px solid #e0e4ed', borderRadius: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
              <Typography variant="subtitle2" sx={{ color: '#1a3072', fontWeight: 'bold', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 1 }}>
                Aeropuertos
              </Typography>
              {selectedAirports.length > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip label={`${selectedAirports.length} seleccionado(s)`} size="small" sx={{ bgcolor: '#1a3072', color: '#fff', fontSize: '0.65rem', height: 20 }} />
                  <Typography
                    variant="caption"
                    sx={{ color: '#e53935', cursor: 'pointer', fontWeight: 600, '&:hover': { textDecoration: 'underline' } }}
                    onClick={() => { setSelectedAirports([]); setIsDirty(true); }}
                  >
                    Limpiar todo
                  </Typography>
                </Box>
              )}
            </Box>

            <Autocomplete
              size="small"
              options={[{ id: -1, code: 'Todos' }, ...airports]}
              getOptionLabel={(o) => o.code || ''}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              value={selectedAirportValue}
              onChange={(_, newValue) => {
                setSelectedAirportValue(newValue);
                if (!newValue) return;
                if (newValue.id === -1) {
                  setSelectedAirports(airports.map(a => ({ id: a.id, code: a.code })));
                  setIsDirty(true);
                  setSelectedAirportValue(null);
                } else {
                  handleAddAirport(newValue);
                }
              }}
              renderInput={(params) => <TextField {...params} label="Agregar aeropuerto" variant="outlined" size="small" />}
              noOptionsText="Sin resultados"
              sx={{ mb: selectedAirports.length > 0 ? 1.5 : 0 }}
            />

            {selectedAirports.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8, maxHeight: 130, overflowY: 'auto', pt: 0.5 }}>
                {selectedAirports.map(a => (
                  <Chip
                    key={a.id}
                    label={a.code}
                    size="small"
                    onDelete={() => { setSelectedAirports(prev => prev.filter(el => el.id !== a.id)); setIsDirty(true); }}
                    sx={{ bgcolor: '#e8f0fe', color: '#1a3072', fontWeight: 600, fontSize: '0.75rem', '& .MuiChip-deleteIcon': { color: '#e53935' } }}
                  />
                ))}
              </Box>
            )}

            {selectedAirports.length === 0 && (
              <Box sx={{ pt: 1.5, textAlign: 'center', color: '#bbb' }}>
                <Typography variant="body2">Sin aeropuertos seleccionados</Typography>
              </Box>
            )}
          </Box>

          {(selectedEmployees.length > 0 && selectedAirports.length > 0) && (
            <Box sx={{ mt: 2, p: 1.5, bgcolor: '#e8f5e9', borderRadius: 2, border: '1px solid #c8e6c9', display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" sx={{ color: '#2e7d32' }}>
                Se asignarán <strong>{selectedAirports.length} aeropuerto(s)</strong> a <strong>{selectedEmployees.length} empleado(s)</strong>.
              </Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ m: 1 }}>
          <Button
            color="primary"
            variant="contained"
            onClick={onSave}
            endIcon={<SaveIcon />}
            disabled={selectedEmployees.length === 0 || selectedAirports.length === 0}
          >
            {ACTIONS.SAVE}
          </Button>
          <Box sx={{ m: 1 }}>
            <Button
              endIcon={<CancelIcon />}
              variant="outlined"
              onClick={() => isDirty ? setShowCancelAlert(true) : handleClose()}
            >
              {ACTIONS.CANCEL}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      <Dialog open={showCancelAlert} maxWidth="xs" fullWidth onClose={() => setShowCancelAlert(false)}>
        <DialogContent sx={{ textAlign: 'center', pt: 4, pb: 2 }}>
          <WarningAmberRoundedIcon sx={{ fontSize: 56, color: '#f59e0b', mb: 1.5 }} />
          <Typography variant="h6" fontWeight="bold" sx={{ color: '#1a3072', mb: 1 }}>
            ¿Descartar cambios?
          </Typography>
          <Typography variant="body2" sx={{ color: '#666' }}>
            Tiene cambios sin guardar. Si sale ahora, se perderán todos los cambios realizados.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', gap: 1, pb: 3 }}>
          <Button variant="outlined" onClick={() => setShowCancelAlert(false)}
            sx={{ minWidth: 140, borderColor: '#1a3072', color: '#1a3072', '&:hover': { borderColor: '#1a3072', bgcolor: '#f0f4ff' } }}>
            Seguir editando
          </Button>
          <Button variant="contained" onClick={() => { setShowCancelAlert(false); handleClose(); }}
            sx={{ minWidth: 140, bgcolor: '#e53935', '&:hover': { bgcolor: '#b71c1c' } }}>
            Salir sin guardar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ManagementAirportBulkAssign;
